import React from "react";
import { ChevronDown, ChevronRight, Plus } from "lucide-react";
import { SidebarMenuButton, SidebarMenuItem } from "@/Components/ui/sidebar";
import { SideButtons } from "./ExplorerSidebar/SideButtons";
import { ListItemConfig } from "../../../configs/listConfig";
import { useAdminStore } from "../../../store/useAdminStore";

type ListType = "stage" | "option" | "item";

interface ConfigurableListProps<T extends { id?: number }> {
   items: T[];
   config: ListItemConfig<T>;
   expandedState: Record<string, boolean>;
   toggleExpand: (key: string) => void;
   level: number;
   stageId?: number;
   optionId?: number;
   moveUp: (
      type: ListType,
      stageIndex?: number,
      optionIndex?: number,
      itemIndex?: number
   ) => void;
   moveDown: (
      type: ListType,
      stageIndex?: number,
      optionIndex?: number,
      itemIndex?: number
   ) => void;
   remove: (
      type: ListType,
      stageIndex?: number,
      optionIndex?: number,
      itemIndex?: number
   ) => void;

   isItemSelected: (
      type: ListType,
      stageId?: number,
      optionId?: number,
      itemId?: number
   ) => boolean;
   stageIndex?: number;
   optionIndex?: number;
}

export function ConfigurableList<T extends { id?: number }>(
   props: ConfigurableListProps<T>
) {
   const {
      items,
      config,
      expandedState,
      toggleExpand,
      level,
      stageId,
      optionId,
      moveUp,
      moveDown,
      remove,
      isItemSelected,
      stageIndex,
      optionIndex,
   } = props;
   const adminStore = useAdminStore();
   const productId = adminStore.selectedProductId;

   const marginLeft = level === 1 ? "ml-6" : level === 2 ? "ml-12" : "";

   return (
      <div className={marginLeft}>
         {items.map((item, index) => {
            const itemKey = `${config.type}-${item.id}`;
            const isExpanded = expandedState[itemKey] || false;
            const stageIdx = config.type === "stage" ? index : stageIndex;
            const optionIdx = config.type === "option" ? index : optionIndex;
            const itemIndex = config.type === "item" ? index : undefined;
            return (
               <div key={item.id}>
                  <SidebarMenuItem>
                     <SidebarMenuButton asChild>
                        <div
                           onClick={() =>
                              config.onSelect(item, stageId, optionId)
                           }
                           className={`w-full flex items-center text-left py-1 px-2 ${
                              isItemSelected(
                                 config.type,
                                 stageId,
                                 optionId,
                                 item.id
                              )
                                 ? "bg-blue-100"
                                 : "hover:bg-gray-100"
                           }`}
                        >
                           {config.canExpand && (
                              <span
                                 onClick={(e) => {
                                    e.stopPropagation();
                                    toggleExpand(itemKey);
                                 }}
                                 className="mr-2 text-gray-500 cursor-pointer"
                              >
                                 {isExpanded ? (
                                    <ChevronDown size={16} />
                                 ) : (
                                    <ChevronRight size={16} />
                                 )}
                              </span>
                           )}
                           <span
                              className={`text-sm flex-1 max-w-32 overflow-hidden whitespace-nowrap overflow-ellipsis ${
                                 !config.canExpand ? "ml-5" : ""
                              }`}
                           >
                              {config.getDisplayName(item)}
                           </span>
                           <SideButtons
                              remove={() =>
                                 remove(
                                    config.type,
                                    stageIdx,
                                    optionIdx,
                                    itemIndex
                                 )
                              }
                              moveDown={() =>
                                 moveDown(
                                    config.type,
                                    stageIdx,
                                    optionIdx,
                                    itemIndex
                                 )
                              }
                              moveUp={() =>
                                 moveUp(
                                    config.type,
                                    stageIdx,
                                    optionIdx,
                                    itemIndex
                                 )
                              }
                              type={config.type}
                              stageIndex={stageIdx}
                              optionIndex={optionIdx}
                              itemIndex={itemIndex}
                           />
                        </div>
                     </SidebarMenuButton>
                  </SidebarMenuItem>

                  {isExpanded && config.getSubItems && config.subListConfig && (
                     <ConfigurableList
                        items={config.getSubItems(item)}
                        config={config.subListConfig}
                        expandedState={expandedState}
                        toggleExpand={toggleExpand}
                        level={level + 1}
                        stageId={config.type === "stage" ? item.id : stageId}
                        optionId={config.type === "option" ? item.id : optionId}
                        moveUp={moveUp}
                        moveDown={moveDown}
                        remove={remove}
                        isItemSelected={isItemSelected}
                        stageIndex={
                           config.type === "stage" ? index : stageIndex
                        }
                        optionIndex={
                           config.type === "option" ? index : optionIndex
                        }
                     />
                  )}
               </div>
            );
         })}
         <div className="ml-2 my-2">
            <button
               onClick={() => config.onAdd(productId!, stageId, optionId)}
               className="text-blue-600 text-sm flex items-center hover:underline"
            >
               <Plus className="w-4 h-4 mr-1" /> Add{" "}
               {config.type.charAt(0).toUpperCase() + config.type.slice(1)}
            </button>
         </div>
      </div>
   );
}
